'use client';
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { useAccount } from '@/contexts/AccountContext';

interface GlobalBalanceContextType {
  balance: number;
  isLoading: boolean;
  deductBalance: (amount: number, userId: string) => Promise<boolean>;
  refreshBalance: () => Promise<void>;
}

const GlobalBalanceContext = createContext<GlobalBalanceContextType>({
  balance: 5000,
  isLoading: true,
  deductBalance: async () => false,
  refreshBalance: async () => {},
});

export function GlobalBalanceProvider({ children }: { children: ReactNode }) {
  const account = useAccount();
  const [balance, setBalance] = useState<number>(5000);
  const [isLoading, setIsLoading] = useState(account.isLoading);

  // Sync with account context
  useEffect(() => {
    setBalance(account.balance);
    setIsLoading(account.isLoading);
  }, [account.balance, account.isLoading]);

  const refreshBalance = useCallback(async () => {
    await account.refreshTransactions();
    setBalance(account.balance);
    setIsLoading(false);
  }, [account]);

  const deductBalance = useCallback(
    async (amount: number, userId: string): Promise<boolean> => {
      return await account.deductBalance(amount, 'Balance deduction', 'withdrawal');
    },
    [account]
  );

  return (
    <GlobalBalanceContext.Provider value={{ balance, isLoading, deductBalance, refreshBalance }}>
      {children}
    </GlobalBalanceContext.Provider>
  );
}

export function useGlobalBalance() {
  return useContext(GlobalBalanceContext);
}
